/**
 * Shared render helpers for inline multiselect (API and static paths).
 * Ensures identical tag look and behavior including delete icon and overflow.
 */

import type { SxProps } from '@mui/material';
import { Chip } from 'components/chip';
import { TagsWithOverflow } from 'components/tags/TagsWithOverflow';
import type { TagWithLabel } from 'components/tags/TagsWithOverflow';

type OptionWithLabel = { id?: number | string; label?: string };

type TagProps = {
  key: string | number;
  onDelete?: (event: unknown) => void;
  sx?: SxProps;
  [key: string]: unknown;
};

/**
 * Horizontal space reserved (px) in edit mode for the inline search input that
 * sits after the tags. The popup caret is reserved separately via CSS
 * (padding-right on .MuiInputBase-root), so this only covers the typing area —
 * keeping the edit-mode +N math close to read mode (DL-60177 / DL-60178).
 */
const EDIT_MODE_INPUT_RESERVE = 36;

/**
 * MUI Autocomplete renderTags implementation for inline multiselect.
 * Uses TagsWithOverflow so edit-mode tags match read-mode (overflow + bordered chips).
 */
export function renderMultiselectTags(
  tagValue: OptionWithLabel[],
  getTagProps: (params: { index: number }) => TagProps,
  colWidth?: number
) {
  const values: TagWithLabel[] = tagValue.map((option, index) => {
    const { onDelete } = getTagProps({ index });

    return {
      id: option.id ?? index,
      label: option.label ?? '',
      ...(typeof onDelete === 'function' ? { onDelete: () => onDelete({}) } : {}),
    };
  });

  return (
    <TagsWithOverflow
      values={values}
      columnWidth={Math.max(1, (colWidth ?? 0) - EDIT_MODE_INPUT_RESERVE)}
    />
  );
}

/**
 * MUI Autocomplete renderTags implementation for inline multiselect.
 * Spreads getTagProps last so onDelete and other MUI props are never overridden.
 */
export function renderMultiselectFieldTags(
  tagValue: OptionWithLabel[],
  getTagProps: (params: { index: number }) => TagProps
) {
  return tagValue.map((option, index) => {
    const { key, sx: tagSx, ...tagProps } = getTagProps({ index });
    return (
      <Chip
        key={key}
        label={option.label}
        size="sm"
        color="white"
        isBordered
        isTooltip={false}
        sx={tagSx}
        {...tagProps}
      />
    );
  });
}
